import {
  Alert,
  Card,
  CardActions,
  CardContent,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { GenSnackBar } from '@pinnacleautomotive/clarity-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessStep } from '../../common/enums/enums';
import ClearFilterBtn from './bulkComponents/ClearFilterBtn';
import CustomDateRange from './bulkComponents/CustomDateRange';
import DateFormat from './bulkComponents/DateFormat';
import DealersFilter from './bulkComponents/DealersFilter';
import DepartmentsFilter from './bulkComponents/DepartmentsFilter';
import ExcludeWOWithCeroPrice from './bulkComponents/ExcludeWOWithCeroPrice';
import ExportBulkButtons from './bulkComponents/ExportBulkButtons';
import QuickDateRange from './bulkComponents/QuickDateRange';
import ServicesFilter from './bulkComponents/ServicesFilter';
import StepsFilter from './bulkComponents/StepsFilter';

const initialState = {
  selectedDate: null,
  dateFormat: 'date',
  selectedDealers: {
    dealers: [],
    allSelected: false,
  },
  selectedDepartments: {
    departments: [],
    allSelected: false,
  },
  selectedServices: {
    services: [],
    allSelected: false,
  },
  selectedSteps: {
    steps: [],
    allSelected: false,
  },
  excludeWo: false,
};

export default function BulkExport() {
  const { t } = useTranslation('bulk');
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const buttonSx = {
    minWidth: 'auto',
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
    background: theme.palette.background.paper,
    padding: '5px 7px',
    textTransform: 'none',
    fontWeight: 400,
  };

  const filtersSx = {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: (smallScreen && '100%') || 'calc(50% - 0.5rem)',
  };

  const [quickDate, setQuickDate] = useState('week');

  const [dateRange, setDateRange] = useState({
    startingDate: null,
    endDate: null,
    formattedDate: {},
  });

  const [selectedFilters, setSelectedFilters] = useState(initialState);

  const [showSnackbar, setShowSnackbar] = useState({
    open: false,
    text: '',
    type: '',
  });

  const [dateError, setDateError] = useState(false);

  const handleDateError = state => {
    setDateError(state);
  };

  useEffect(() => {
    if (selectedFilters.dateFormat === 'completedOn')
      setSelectedFilters(state => ({
        ...state,
        selectedSteps: {
          steps: [ProcessStep.DONE],
          allSelected: false,
        },
      }));
  }, [selectedFilters.dateFormat]);

  return (
    <>
      <GenSnackBar
        title={t(showSnackbar.text)}
        type={showSnackbar.type}
        open={showSnackbar.open}
        onClose={() =>
          setShowSnackbar({
            open: false,
            text: '',
            type: '',
          })
        }
      />
      <Stack pt={3} alignItems="center" px={2}>
        <Stack gap={2} maxWidth="635px" width="100%">
          <Stack>
            <Typography variant="h6">{t('title')}</Typography>
            <Typography variant="body1">{t('subtitle')}</Typography>
            <Alert sx={{ marginTop: 2 }} severity="info">
              {t('alert')}
            </Alert>
          </Stack>
          <Card variant="outlined">
            <CardContent>
              <Stack gap={2}>
                <Stack gap={1.5}>
                  <Typography variant="subtitle2">
                    {t('selectDateRange')}
                  </Typography>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'row',
                    }}
                    alignItems="baseline"
                    gap={{
                      sm: 2,
                      xs: 1,
                    }}
                    divider={<Divider flexItem orientation="vertical" />}
                  >
                    <QuickDateRange
                      buttonSx={buttonSx}
                      quickDate={quickDate}
                      setQuickDate={setQuickDate}
                      setDateRange={setDateRange}
                      setSelectedFilters={setSelectedFilters}
                    />
                    <CustomDateRange
                      dateRange={dateRange}
                      setDateRange={setDateRange}
                      setQuickDate={setQuickDate}
                      setSelectedFilters={setSelectedFilters}
                      handleDateError={handleDateError}
                    />
                  </Stack>
                </Stack>
                <Stack gap={1.5}>
                  <Typography variant="subtitle2">{t('dateFormat')}</Typography>
                  <DateFormat
                    buttonSx={buttonSx}
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                  />
                </Stack>
                <Stack gap={1.5}>
                  <Typography variant="subtitle2">{t('filters')}</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={2}>
                    <DealersFilter
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                    />
                    <DepartmentsFilter
                      filtersSx={filtersSx}
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                    />
                    <ServicesFilter
                      filtersSx={filtersSx}
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                    />
                    <StepsFilter
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                    />
                    <ExcludeWOWithCeroPrice
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
            <CardActions
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                p: 2,
                borderTop: '1px solid',
                borderColor: theme.palette.divider,
                flexWrap: 'wrap',
              }}
            >
              <ClearFilterBtn
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                initState={initialState}
                setQuickDate={setQuickDate}
              />
              <ExportBulkButtons
                selectedFilters={selectedFilters}
                setShowSnackbar={setShowSnackbar}
                dateError={dateError}
              />
            </CardActions>
          </Card>
        </Stack>
      </Stack>
    </>
  );
}
